import React, { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2, Save, X, MapPin, CreditCard, AlertTriangle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import api, { formatApiError } from "../api";
import { Button, Card, Input, Label, Select, Spinner, EmptyState, Badge } from "../components/ui/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { useOutlets } from "../components/OutletContext";

const TYPE_LABELS = {
  restaurant: "Restaurant",
  bar: "Bar",
  hotel: "Hotel",
  cafe: "Café",
  catering: "Catering",
  other: "Sonstiges",
};

export default function OutletsPage() {
  const { user } = useAuth();
  const { outlets, meta, reload, loading } = useOutlets();
  const [editing, setEditing] = useState(null); // null | "new" | outlet
  const isPremium = user?.plan === "premium" || user?.plan === "enterprise";
  const isEnterprise = user?.plan === "enterprise";

  const remove = async (id) => {
    if (!window.confirm("Outlet wirklich löschen? Alle zugewiesenen Daten (Zutaten, Rezepte, Cocktails) bleiben sichtbar, sind aber dem Outlet zugeordnet.")) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success("Outlet gelöscht");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Spinner size={28} /></div>;

  return (
    <div className="space-y-6" data-testid="outlets-page">
      <header>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-brand font-medium mb-1">Multi-Outlet</div>
            <h1 className="text-2xl font-semibold tracking-tight">Outlets</h1>
            <p className="text-sm text-ink-secondary mt-1" data-testid="outlet-quota-display">
              {meta.is_unlimited
                ? <>{outlets.length} Outlets · <span className="text-brand font-medium">Unbegrenzt (Enterprise)</span></>
                : <>{outlets.length} von {meta.max_allowed} Outlets genutzt · {meta.included} im Plan enthalten · {meta.remaining_slots || 0} verfügbar</>}
            </p>
          </div>
          {(isPremium || meta.is_unlimited) && (meta.is_unlimited || outlets.length < (meta.max_allowed ?? 0)) && (
            <Button onClick={() => setEditing("new")} data-testid="outlet-new-btn">
              <Plus size={14} /> Outlet anlegen
            </Button>
          )}
        </div>
        {!isPremium && (
          <Card className="mt-4 p-4 border-amber-500/30 bg-amber-500/[0.05] flex items-start gap-3" data-testid="outlets-upgrade-hint">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-ink-primary">Multi-Outlet ist Premium-exklusiv</div>
              <div className="text-xs text-ink-secondary mt-0.5">
                Upgrade auf Premium (€289/Mo) für 2 Outlets inkl. Bis zu 5 Outlets möglich (+€199/Mo je zusätzlich).
                {" "}<a href="/billing/plan" className="text-brand hover:underline">Plan ändern →</a>
              </div>
            </div>
          </Card>
        )}
      </header>

      <Card className="overflow-hidden">
        {outlets.length === 0 ? (
          <EmptyState icon={Building2} title="Noch keine Outlets" description="Legen Sie Ihren ersten Standort an." />
        ) : (
          <table className="w-full text-sm" data-testid="outlets-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Typ</th>
                <th className="text-left px-4 py-3">Adresse</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {outlets.map((o) => (
                <tr key={o.id} data-testid={`outlet-row-${o.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-primary">{o.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{TYPE_LABELS[o.type] || o.type}</td>
                  <td className="px-4 py-3 text-ink-muted text-xs">
                    <span className="inline-flex items-center gap-1"><MapPin size={11} /> {o.address || "—"}</span>
                  </td>
                  <td className="px-4 py-3">{o.is_default ? <Badge tone="brand">Standard</Badge> : <Badge>Zusätzlich</Badge>}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(o)} data-testid={`outlet-edit-${o.id}`}><Pencil size={11} /></Button>
                    {!o.is_default && isPremium && (
                      <Button size="sm" variant="ghost" onClick={() => remove(o.id)} data-testid={`outlet-delete-${o.id}`}>
                        <Trash2 size={11} className="text-err" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {isPremium && !isEnterprise && <OutletPricingCard outlets={outlets.length} />}

      {editing && (
        <OutletFormModal
          outlet={editing === "new" ? null : editing}
          outlets={outlets}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

function OutletPricingCard({ outlets }) {
  const [additional, setAdditional] = useState(Math.max(0, outlets - 2));
  const [preview, setPreview] = useState(null);
  const [interval, setInterval] = useState("monthly");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/outlets/pricing-preview", { params: { additional } });
        if (alive) setPreview(data);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [additional]);

  if (!preview) return null;
  const price = interval === "monthly" ? preview.monthly_eur : preview.annual_eur;

  return (
    <Card className="p-5 space-y-4" data-testid="outlet-pricing-card">
      <div>
        <h3 className="text-sm font-semibold tracking-tight">Outlet-Kalkulator</h3>
        <p className="text-xs text-ink-secondary mt-0.5">{preview.summary_de}</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="!mb-0">Zusätzliche Outlets:</Label>
        {[0, 1, 2, 3].map((n) => (
          <button
            key={n}
            onClick={() => setAdditional(n)}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
              additional === n ? "border-brand bg-brand/10 text-brand" : "border-white/10 text-ink-secondary hover:text-ink-primary"
            }`}
            data-testid={`pricing-additional-${n}`}
          >
            +{n}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => setInterval("monthly")}
            className={`px-2 py-1 rounded text-xs ${interval === "monthly" ? "bg-brand/15 text-brand" : "text-ink-muted"}`}
            data-testid="pricing-monthly"
          >Monatlich</button>
          <button
            onClick={() => setInterval("annual")}
            className={`px-2 py-1 rounded text-xs ${interval === "annual" ? "bg-brand/15 text-brand" : "text-ink-muted"}`}
            data-testid="pricing-annual"
          >Jährlich -15%</button>
        </div>
      </div>
      <div className="border-t border-white/5 pt-3 flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{preview.total_outlets} Outlets · {interval === "monthly" ? "monatlich" : "jährlich"}</div>
          <div className="num text-2xl font-semibold text-ink-primary" data-testid="pricing-total">
            € {price.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <a href={`/billing/plan?plan=premium&interval=${interval}&additional=${additional}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors" data-testid="pricing-cta">
          <CreditCard size={14} /> Upgrade & buchen
        </a>
      </div>
    </Card>
  );
}

function OutletFormModal({ outlet, outlets = [], onClose, onSaved }) {
  const isNew = !outlet;
  const otherOutlets = (outlets || []).filter((o) => o.id !== outlet?.id);
  const [form, setForm] = useState({
    name: outlet?.name || "",
    type: outlet?.type || "restaurant",
    address: outlet?.address || "",
  });
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateSourceId, setTemplateSourceId] = useState(otherOutlets[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (outlet) {
        await api.patch(`/outlets/${outlet.id}`, form);
        toast.success("Outlet gespeichert");
        onSaved();
      } else {
        const { data } = await api.post("/outlets", form);
        const newId = data?.outlet?.id;
        if (useTemplate && templateSourceId && newId) {
          try {
            const { data: tpl } = await api.post(`/outlets/${newId}/apply-template`, {
              source_outlet_id: templateSourceId,
            });
            const c = tpl?.counts || {};
            toast.success(
              `Outlet angelegt — Vorlage übernommen: ${c.ingredients || 0} Zutaten · ${c.recipes || 0} Rezepte · ${c.cocktails || 0} Cocktails · ${c.menus || 0} Menüs (Preise leer)`,
              { duration: 6000 }
            );
          } catch (tplErr) {
            toast.error("Outlet angelegt, aber Vorlage fehlgeschlagen: " + formatApiError(tplErr));
          }
        } else {
          toast.success("Outlet angelegt");
        }
        onSaved();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-bg-surface border border-white/10 rounded-2xl max-w-md w-full p-6 shadow-glow space-y-4"
        data-testid="outlet-form-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold tracking-tight">{outlet ? "Outlet bearbeiten" : "Neues Outlet"}</h3>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div>
          <Label>Name</Label>
          <Input required value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="outlet-form-name" />
        </div>
        <div>
          <Label>Typ</Label>
          <Select value={form.type} onChange={(e) => setForm((s) => ({ ...s, type: e.target.value }))} data-testid="outlet-form-type">
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </div>
        <div>
          <Label>Adresse</Label>
          <Input value={form.address} onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))} data-testid="outlet-form-address" />
        </div>

        {isNew && otherOutlets.length > 0 && (
          <div className="border-t border-white/10 pt-4 space-y-3" data-testid="outlet-template-section">
            <Label className="!mb-0">Struktur aus bestehendem Outlet übernehmen?</Label>
            <p className="text-[11px] text-ink-muted leading-relaxed">
              Kopiert Kategorien, Rezept- und Cocktail-Namen, Zutatenlisten und Menü-Aufbau.
              Preise, Kosten und lokale Lieferanten-Daten bleiben leer und werden pro Outlet einzeln gepflegt.
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="outlet-template-blank">
                <input
                  type="radio"
                  name="tpl-choice"
                  checked={!useTemplate}
                  onChange={() => setUseTemplate(false)}
                  className="mt-1"
                />
                <span><span className="text-ink-primary">Komplett leer starten</span>
                  <span className="block text-[11px] text-ink-muted">Keine Daten — alles wird von Grund auf neu angelegt.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="outlet-template-clone">
                <input
                  type="radio"
                  name="tpl-choice"
                  checked={useTemplate}
                  onChange={() => setUseTemplate(true)}
                  className="mt-1"
                />
                <span><span className="text-ink-primary">Struktur von bestehendem Outlet übernehmen</span>
                  <span className="block text-[11px] text-ink-muted">Nur Struktur — keine Preise, keine Kosten, keine Adressen.</span>
                </span>
              </label>
            </div>
            {useTemplate && (
              <div className="pl-6">
                <Label>Quell-Outlet</Label>
                <Select
                  value={templateSourceId}
                  onChange={(e) => setTemplateSourceId(e.target.value)}
                  data-testid="outlet-template-source"
                >
                  {otherOutlets.map((o) => (
                    <option key={o.id} value={o.id}>{o.name} · {TYPE_LABELS[o.type] || o.type}</option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button type="submit" disabled={busy} data-testid="outlet-form-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </Button>
        </div>
      </form>
    </div>
  );
}
